/**
 * Member Detail Modal
 * Unified modal for both approving pending members and editing existing members
 */

import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { updateMember, rejectMember, deleteMember, resetMemberPassword } from '@/lib/api/members';
import { DeleteMemberDialog } from '@/components/admin/DeleteMemberDialog';
import { fetchMenus } from '@/lib/api/menus';
import { adminOverrideRSVP, fetchRSVPs } from '@/lib/api/rsvps';
import { getWeekRange, nextWeek, toISODate } from '@/lib/utils/dateHelpers';
import { isMenuOverrideEligible } from '@/lib/utils/menuHelpers';
import { toggleOptOut } from '@/lib/utils/optOutHelpers';
import { LocationColorDot } from '@/components/ui/location-color-dot';
import { OptionalRecipeToggles } from '@/components/rsvp/OptionalRecipeToggles';
import { SizeSelector } from '@/components/rsvp/SizeSelector';
import type { Member, PickupLocation, Menu } from '@/types/api';
import { RSVP_SIZES, RSVP_SIZE_LABELS, type RSVPSizeCode } from '@/types/units';

// Type aliases for Prisma enums
type MemberStatus = 'PENDING' | 'ACTIVE' | 'INACTIVE';
type MemberRole = 'MEMBER' | 'ADMIN' | 'CATERER';

interface MemberDetailModalProps {
  member: Member | null;
  pickupLocations: PickupLocation[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface MemberDetailContentProps extends MemberDetailModalProps {
  surface: 'dialog' | 'drawer';
}

interface MemberFormData {
  name: string;
  email: string;
  phone: string;
  thaaliNumber: string;
  pickupLocationId: string;
  defaultSize: string;
  mithaas: boolean;
  role: MemberRole;
  status: MemberStatus;
}

function getMemberPickupLocationId(member: Member): number | null {
  return member.pickupLocationId ?? member.pickupLocation?.id ?? null;
}

function getMemberFormDefaults(member: Member): MemberFormData {
  return {
    name: member.name || '',
    email: member.email || '',
    phone: member.phone || '',
    thaaliNumber: member.thaaliNumber || '',
    pickupLocationId: getMemberPickupLocationId(member)?.toString() || '',
    defaultSize: member.defaultSize || 'M',
    mithaas: member.mithaas !== false,
    role: member.role || 'MEMBER',
    status: member.status || 'ACTIVE',
  };
}

// Mirrors the server's passwordSchema (server/lib/validation.ts) so admins get
// a specific message instead of a generic "Validation failed" from the API.
function getPasswordValidationError(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return null;
}

// Ambiguous characters (0/O, 1/l/I) are excluded since the password is
// typically read aloud or copied by hand to the member.
function generateTemporaryPassword(): string {
  const upper = 'ABCDEFGHJKMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const all = upper + lower + digits;

  const randomValues = new Uint32Array(12);
  crypto.getRandomValues(randomValues);
  const pick = (chars: string, seed: number) => chars[seed % chars.length];

  // First characters guarantee the uppercase + number requirements; the rest
  // are drawn from the full set.
  const chars = [
    pick(upper, randomValues[0]!),
    pick(digits, randomValues[1]!),
    ...Array.from(randomValues.slice(2), (seed) => pick(all, seed)),
  ];
  return chars.join('');
}

function getMemberDetailCopy(member: Member) {
  const isPending = member.status === 'PENDING';
  return {
    title: isPending ? 'Approve Member' : 'Edit Member',
    description: isPending
      ? 'Review and approve member registration'
      : 'Update member information',
  };
}

function MemberDetailContent({
  member,
  pickupLocations,
  isOpen,
  onClose,
  onSuccess,
  surface,
}: MemberDetailContentProps) {
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideMenus, setOverrideMenus] = useState<Menu[]>([]);
  const [overrideMenuId, setOverrideMenuId] = useState('');
  const [overrideSize, setOverrideSize] = useState<RSVPSizeCode | 'SKIP'>('M');
  const [overrideOptOuts, setOverrideOptOuts] = useState<Set<number>>(new Set());
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const { showToast } = useUIStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<MemberFormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      thaaliNumber: '',
      pickupLocationId: '',
      defaultSize: 'M',
      mithaas: true,
      role: 'MEMBER',
      status: 'ACTIVE',
    },
  });

  const isPending = member?.status === 'PENDING';
  // Admins change their own password from the Profile page (server refuses self-reset)
  const currentUserId = useAuthStore((state) => state.user?.id);
  const isOwnRecord = member !== null && String(member.id) === currentUserId;
  const watchedRole = watch('role');
  const watchedStatus = watch('status');
  const watchedPickupLocationId = watch('pickupLocationId');
  const watchedDefaultSize = watch('defaultSize');
  const watchedMithaas = watch('mithaas');

  const overrideMenuOptions = useMemo(() => {
    return overrideMenus.filter((menu) => isMenuOverrideEligible(menu));
  }, [overrideMenus]);

  const selectedOverrideMenu = useMemo(() => {
    return overrideMenuOptions.find((menu) => menu.id.toString() === overrideMenuId) || null;
  }, [overrideMenuId, overrideMenuOptions]);

  const memberDefaults = useMemo(() => (member ? getMemberFormDefaults(member) : null), [member]);
  const visiblePickupLocations = useMemo(() => {
    const currentPickupLocationId = member ? getMemberPickupLocationId(member) : null;
    return pickupLocations.filter((loc) => loc.isActive || loc.id === currentPickupLocationId);
  }, [member, pickupLocations]);

  // Reset form when member changes
  useEffect(() => {
    if (memberDefaults && isOpen) {
      reset(memberDefaults);
      setOverrideMenuId('');
      setOverrideSize('M');
      setOverrideOptOuts(new Set());
      setNewPassword('');
      setNewPasswordError(null);
    }
  }, [memberDefaults, isOpen, reset]);

  useEffect(() => {
    const loadOverrideMenus = async () => {
      if (!member || !isOpen || isPending) return;
      try {
        setOverrideLoading(true);
        // Only fetch current and next week - override is only relevant when cutoff has passed
        const now = new Date();
        const { start } = getWeekRange(now);
        const { end } = getWeekRange(nextWeek(now));
        const menusData = await fetchMenus(toISODate(start), toISODate(end));
        setOverrideMenus(menusData);
        const firstAvailable = menusData.find((menu) => isMenuOverrideEligible(menu));
        if (firstAvailable && !overrideMenuId) {
          setOverrideMenuId(firstAvailable.id.toString());
        }
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : 'Failed to load menus',
          'error'
        );
      } finally {
        setOverrideLoading(false);
      }
    };

    loadOverrideMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overrideMenuId intentionally excluded to only auto-select on initial load
  }, [member, isOpen, isPending, showToast]);

  // Hydrate opt-outs from member's existing RSVP when override menu changes
  useEffect(() => {
    if (!member || !overrideMenuId) {
      setOverrideOptOuts(new Set());
      return;
    }

    let cancelled = false;
    const loadExistingOptOuts = async () => {
      try {
        const menuId = Number.parseInt(overrideMenuId, 10);
        const { rsvps } = await fetchRSVPs({
          memberId: member.id.toString(),
          menuId,
        });
        if (cancelled) return;
        const existingRsvp = rsvps.find(
          (r) => r.menuId === menuId && r.status === 'ACTIVE'
        );
        if (existingRsvp?.optedOutMenuRecipeIds?.length) {
          setOverrideOptOuts(new Set(existingRsvp.optedOutMenuRecipeIds));
        } else {
          setOverrideOptOuts(new Set());
        }
      } catch {
        // Non-critical - start with empty opt-outs on fetch failure
        if (!cancelled) setOverrideOptOuts(new Set());
      }
    };

    loadExistingOptOuts();
    return () => { cancelled = true; };
  }, [member, overrideMenuId]);

  const onSubmit = async (data: MemberFormData) => {
    if (!member) return;

    try {
      setSubmitting(true);

      const currentDefaults = getMemberFormDefaults(member);
      const pickupLocationId = data.pickupLocationId || currentDefaults.pickupLocationId;

      const updateData: any = {
        name: data.name,
        email: data.email || null,
        phone: data.phone || null,
        thaaliNumber: data.thaaliNumber || null,
        pickupLocationId: pickupLocationId ? parseInt(pickupLocationId, 10) : null,
        defaultSize: data.defaultSize || currentDefaults.defaultSize || null,
        mithaas: data.mithaas,
        role: data.role || currentDefaults.role,
      };

      // For pending members being approved, set status to ACTIVE
      if (isPending) {
        updateData.status = 'ACTIVE';

        // Validate required fields for approval
        if (!data.thaaliNumber) {
          showToast('Thaali number is required for approval', 'error');
          return;
        }
      } else {
        // For editing existing members, include status
        updateData.status = data.status || currentDefaults.status;
      }

      await updateMember(member.id.toString(), updateData);

      showToast(
        isPending ? 'Member approved successfully' : 'Member updated successfully',
        'success'
      );

      reset();
      onSuccess();
      onClose();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update member',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!member) return;

    try {
      setSubmitting(true);
      await rejectMember(member.id.toString());
      showToast('Member rejected successfully', 'success');
      reset();
      onSuccess();
      onClose();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to reject member',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!member) return;

    try {
      setDeleting(true);
      await deleteMember(member.id.toString());
      showToast('Member deleted permanently', 'success');
      setDeleteDialogOpen(false);
      reset();
      onSuccess();
      onClose();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to delete member',
        'error'
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!member) return;

    const validationError = getPasswordValidationError(newPassword);
    if (validationError) {
      setNewPasswordError(validationError);
      return;
    }

    try {
      setResettingPassword(true);
      setNewPasswordError(null);
      await resetMemberPassword(member.id.toString(), newPassword);
      showToast('Password reset — share the new password with the member', 'success');
      setNewPassword('');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to reset password',
        'error'
      );
    } finally {
      setResettingPassword(false);
    }
  };

  const handleOverrideSubmit = async () => {
    if (!member || !overrideMenuId) {
      showToast('Select a menu date first', 'error');
      return;
    }

    try {
      setOverrideSubmitting(true);
      await adminOverrideRSVP(member.id.toString(), {
        menuId: Number.parseInt(overrideMenuId, 10),
        size: overrideSize,
        optedOutMenuRecipeIds: overrideSize !== 'SKIP' ? Array.from(overrideOptOuts) : [],
      });
      showToast('RSVP override saved', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to override RSVP',
        'error'
      );
    } finally {
      setOverrideSubmitting(false);
    }
  };

  if (!member) {
    return null;
  }

  const submitText = isPending ? 'Approve' : 'Save Changes';
  const submittingText = isPending ? 'Approving...' : 'Saving...';
  const formActionsClassName = surface === 'drawer'
    ? 'flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end'
    : 'flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end';

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="min-w-0 max-w-full space-y-4">
            {/* ITS ID (read-only) */}
            <div className="space-y-2">
              <Label htmlFor="itsId">ITS ID</Label>
              <Input
                id="itsId"
                value={member.id.toString()}
                disabled
                className="bg-gray-50"
              />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                {...register('name', { required: 'Name is required' })}
                placeholder="Full name"
                disabled={submitting}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                placeholder="email@example.com (optional)"
                disabled={submitting}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                {...register('phone')}
                placeholder="Phone number (optional)"
                disabled={submitting}
              />
            </div>

            {/* Thaali Number */}
            <div className="space-y-2">
              <Label htmlFor="thaaliNumber">
                Thaali Number {isPending && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="thaaliNumber"
                {...register('thaaliNumber', {
                  required: isPending ? 'Thaali number is required for approval' : false,
                })}
                placeholder="Thaali number"
                disabled={submitting}
              />
              {errors.thaaliNumber && (
                <p className="text-sm text-red-500">{errors.thaaliNumber.message}</p>
              )}
            </div>

            {/* Pickup Location */}
            <div className="space-y-2">
              <Label htmlFor="pickupLocation">Pickup Location</Label>
              <Select
                value={watchedPickupLocationId || memberDefaults?.pickupLocationId || ''}
                onValueChange={(value) => {
                  if (value) setValue('pickupLocationId', value);
                }}
                disabled={submitting}
              >
                <SelectTrigger id="pickupLocation" className="w-full">
                  <SelectValue placeholder="Select pickup location" />
                </SelectTrigger>
                <SelectContent>
                  {visiblePickupLocations
                    .map((location) => (
                      <SelectItem key={location.id} value={location.id.toString()}>
                        <span className="flex items-center gap-2">
                          <LocationColorDot colorCode={location.colorCode} />
                          {location.name}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Default Size */}
            <div className="space-y-2">
              <Label>Default Thaali Size</Label>
              <div className="flex flex-wrap gap-4">
                {RSVP_SIZES.map((size) => (
                  <label key={size} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value={size}
                      checked={watchedDefaultSize === size}
                      onChange={(e) => setValue('defaultSize', e.target.value)}
                      disabled={submitting}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="text-sm">{RSVP_SIZE_LABELS[size]} ({size})</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Mithaas */}
            <div className="space-y-2">
              <Label>Include Mithaas</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="yes"
                    name="mithaas"
                    checked={watchedMithaas === true}
                    onChange={() => setValue('mithaas', true)}
                    disabled={submitting}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="text-sm">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="no"
                    name="mithaas"
                    checked={watchedMithaas === false}
                    onChange={() => setValue('mithaas', false)}
                    disabled={submitting}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="text-sm">No</span>
                </label>
              </div>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={watchedRole || memberDefaults?.role || 'MEMBER'}
                onValueChange={(value) => {
                  if (value) setValue('role', value as MemberRole);
                }}
                disabled={submitting}
              >
                <SelectTrigger id="role" className="w-full">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MEMBER">Member</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="CATERER">Caterer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status (only for editing, not for approval) */}
            {!isPending && (
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={watchedStatus || memberDefaults?.status || 'ACTIVE'}
                  onValueChange={(value) => {
                    // Radix Select can fire a spurious onValueChange('') when its
                    // controlled value changes programmatically (e.g. after the
                    // form reset). No item has an empty value, so ignore it.
                    if (value) setValue('status', value as MemberStatus);
                  }}
                  disabled={submitting}
                >
                  <SelectTrigger id="status" className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className={formActionsClassName}>
              {isPending && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleReject}
                  disabled={submitting || deleting}
                >
                  Reject
                </Button>
              )}
              <Button type="submit" disabled={submitting || deleting}>
                {submitting ? submittingText : submitText}
              </Button>
            </div>
      </form>

      {!isPending && (
        <div className="mt-6 pt-6 border-t space-y-4">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold">Admin RSVP Override (Single Day)</h3>
                <p className="text-sm text-muted-foreground">
                  Overrides are logged if cutoff has passed.
                </p>
              </div>

              {overrideLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : overrideMenuOptions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No upcoming menus with cutoff passed.
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="overrideMenu">Menu date</Label>
                    <Select
                      value={overrideMenuId}
                      onValueChange={setOverrideMenuId}
                      disabled={overrideSubmitting}
                    >
                      <SelectTrigger id="overrideMenu" className="w-full">
                        <SelectValue placeholder="Select menu date" />
                      </SelectTrigger>
                      <SelectContent>
                        {overrideMenuOptions.map((menu) => (
                          <SelectItem key={menu.id} value={menu.id.toString()}>
                            {menu.date} — {menu.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>RSVP size</Label>
                    <SizeSelector
                      value={overrideSize}
                      onChange={(size) => {
                        if (size) setOverrideSize(size);
                      }}
                      disabled={overrideSubmitting}
                      className="w-full sm:w-fit"
                    />
                  </div>

                  {selectedOverrideMenu && overrideSize !== 'SKIP' && selectedOverrideMenu.recipes.some((r) => r.isOptional) && (
                    <div className="space-y-2">
                      <Label>Optional recipes</Label>
                      <OptionalRecipeToggles
                        recipes={selectedOverrideMenu.recipes}
                        optedOutIds={overrideOptOuts}
                        onChange={(menuRecipeId, included) => {
                          setOverrideOptOuts((prev) => toggleOptOut(prev, menuRecipeId, included));
                        }}
                        disabled={overrideSubmitting}
                      />
                    </div>
                  )}

                  {selectedOverrideMenu?.rsvpCutoffPassed && (
                    <p className="text-sm text-muted-foreground">
                      Cutoff passed for this week — override will be logged.
                    </p>
                  )}

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={handleOverrideSubmit}
                      disabled={overrideSubmitting || deleting}
                    >
                      {overrideSubmitting ? 'Saving...' : 'Save Override'}
                    </Button>
                  </div>
                </div>
              )}
        </div>
      )}

      {!isPending && !isOwnRecord && (
        <div className="mt-6 pt-6 border-t space-y-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Reset Password</h3>
            <p className="text-sm text-muted-foreground">
              Set a new password for a member who has lost access to their
              account. Share it with them securely and ask them to change it
              from their profile after logging in.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resetPassword">New password</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="resetPassword"
                type="text"
                autoComplete="off"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (newPasswordError) setNewPasswordError(null);
                }}
                placeholder="At least 8 characters, one uppercase letter, one number"
                disabled={resettingPassword}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setNewPassword(generateTemporaryPassword());
                  setNewPasswordError(null);
                }}
                disabled={resettingPassword}
              >
                Generate
              </Button>
            </div>
            {newPasswordError && (
              <p className="text-sm text-red-500">{newPasswordError}</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleResetPassword}
              disabled={resettingPassword || deleting || !newPassword}
            >
              {resettingPassword ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </div>
      )}

      {/* Danger zone: only members already saved as INACTIVE can be deleted.
          Also hide it while the form's unsaved Status is anything else, so an
          admin mid-reactivation isn't shown a live Delete button. */}
      {member.status === 'INACTIVE' && watchedStatus === 'INACTIVE' && (
        <div className="mt-6 pt-6 border-t space-y-3">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-destructive">Delete Member</h3>
            <p className="text-sm text-muted-foreground">
              Permanently remove this member along with their RSVP and feedback
              history. This cannot be undone.
            </p>
          </div>
          <Button
            type="button"
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={submitting || deleting}
          >
            Delete Member
          </Button>
        </div>
      )}

      <DeleteMemberDialog
        member={member}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        isDeleting={deleting}
      />
    </>
  );
}

export function MemberDetailModal(props: MemberDetailModalProps) {
  const { member, pickupLocations, isOpen, onClose, onSuccess } = props;

  if (!member) {
    return null;
  }

  const { title, description } = getMemberDetailCopy(member);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <MemberDetailContent
          member={member}
          pickupLocations={pickupLocations}
          isOpen={isOpen}
          onClose={onClose}
          onSuccess={onSuccess}
          surface="dialog"
        />
      </DialogContent>
    </Dialog>
  );
}

export function MemberDetailDrawer(props: MemberDetailModalProps) {
  const { member, pickupLocations, isOpen, onClose, onSuccess } = props;

  if (!member) {
    return null;
  }

  const { title, description } = getMemberDetailCopy(member);

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent
        onSwipeDown={onClose}
        description={description}
      >
        <DrawerHeader className="border-b">
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>{description}</DrawerDescription>
        </DrawerHeader>
        <DrawerBody>
          <MemberDetailContent
            member={member}
            pickupLocations={pickupLocations}
            isOpen={isOpen}
            onClose={onClose}
            onSuccess={onSuccess}
            surface="drawer"
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
